import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    Radio,
    RadioGroup,
    SelectChangeEvent,
    Theme,
    Typography
} from "@mui/material";
import { CurrencyConversionToWords, CustomTooltip } from "../commonComponents";
import React, { useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import { MFTextField } from "../../../../lib/formik";
import { TextDatePicker } from "../../../onboarding/DatePickerWithTextFeild";
import MFSelectField from "../../../../lib/formik/SelectField";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import {
    FormikContextType,
    useFormikContext
} from '../../../../lib/formik/Formik';
import {
    allowDecimalNumbers,
    formatCurrency,
    preventSpecialCharacters,
    resolvePanPlaceholder,
} from "../../../../utils/utilityFunctions";
import dayjs from "../../../../utils/dayjs";
import FormikSearchDropdown from "../../../../lib/formik/FormikSearchDropdown";
import * as yup from 'yup';
import { GetHoldingCommitmentInfoResponseBody } from "../../../../redux-store/types/orderManagement";

export default function AifForm({
    securitiesData,
    fetchSecuritiesData,
    setValidations,
    isLoading,
    fetchHoldingCommitmentInfo
}: {
    securitiesData: Record<string, any>;
    fetchSecuritiesData: (searchInput: string) => void;
    setValidations: (values: Record<string, any>) => void;
    isLoading: boolean;
    fetchHoldingCommitmentInfo: (security: string, portfolio: string, folioNo: string) => Promise<GetHoldingCommitmentInfoResponseBody>;
}): JSX.Element {
    const { currencySymbol, locale = "en-IN", currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const { businessDate = "" } = useSelector((store: RootStateType) => store.navbarInfo);

    const {
        values,
        setFieldValue,
        resetForm
    }: FormikContextType<Record<string, any>> = useFormikContext();

    // Tracks whether the holdingCommitmentInfo API call is in flight
    const [isFetchingCommitmentInfo, setIsFetchingCommitmentInfo] = useState(false);
    // Stores the API-fetched commitment amount for Top Up (displayed in the label only, not filled into the field)
    const [topUpPastCommitmentAmount, setTopUpPastCommitmentAmount] = useState<number | null>(null);

    const handleDeferralExceptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue("deferralException", event.target.value)
    };

    const handleOrderDateChange = (selectedDate: any) => {
        setFieldValue('orderDate', selectedDate?.isValid() ? selectedDate : "", true)
    }

    const handleCurrencyFieldPaste = (event: React.ClipboardEvent<HTMLInputElement>, fieldName: string) => {
        event.preventDefault()
        const clipboardData = event.clipboardData.getData("text")

        const modifiedText = (`${clipboardData ?? ""}`)?.replaceAll(",", "")

        setFieldValue(fieldName, modifiedText);
    }

    const handleTransactionTypeChange = async (event: SelectChangeEvent<unknown>) => {
        if (event.target.value === "Initial Contribution") {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                quantity: yup.string(),
                commitmentAmount: yup
                    .string()
                    .transform(function (value) {
                        return `${value ?? ""}`?.replaceAll(',', '')
                    })
                    .required('Commitment Amount is required')
                    .test('is-number', 'Commitment Amount should be a valid number', function (value) {
                        return !Number.isNaN(Number(value))
                    })
                    .test('greater-than-zero', "Commitment Amount should be greater than 0", function (value) {
                        return Number(value) > 0
                    }),
                // .test('whole-number', 'Commitment Amount should be a valid number', function (value) {
                //     return numberRegex.test(`${value ?? ""}`);
                // }),
                amount: yup
                    .string()
                    .transform(function (value) {
                        return `${value ?? ""}`?.replaceAll(',', '')
                    })
                    .required('Amount is required')
                    .test('is-number', 'Amount should be a valid number', function (value) {
                        return !Number.isNaN(Number(value))
                    })
                    .test('greater-than-zero', "Amount should be greater than 0", function (value) {
                        return Number(value) > 0
                    })
                    // .test('whole-number', 'Amount should be a valid number', function (value) {
                    //     return numberRegex.test(`${value ?? ""}`);
                    // })
                    .test("less-than-equal-to-commitment-amount", 'Amount should not be more than commitment amount', function (value, context) {
                        const { parent } = context;
                        return Number(value) <= Number(`${parent?.commitmentAmount ?? ""}`?.replaceAll(',', ''))
                    })
            }))
        }

        if (event.target.value === "Drawdown Contribution") {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                quantity: yup.string(),
                // NOTE (AIF-CA-01): Drawdown Contribution commitment amount is always API-sourced and
                // the field is always disabled — even if the API fails. This is intentional.
                // The user cannot manually enter a value for this transaction type.
                commitmentAmount: yup.string(),
                amount: yup
                    .string()
                    .transform(function (value) {
                        return `${value ?? ""}`?.replaceAll(',', '')
                    })
                    .required('Amount is required')
                    .test('is-number', 'Amount should be a valid number', function (value) {
                        return !Number.isNaN(Number(value))
                    })
                    .test('greater-than-zero', "Amount should be greater than 0", function (value) {
                        return Number(value) > 0
                    })
                    // .test('whole-number', 'Amount should be a valid number', function (value) {
                    //     return numberRegex.test(`${value ?? ""}`);
                    // })
                    .test("less-than-equal-to-commitment-amount", 'Amount should not be more than commitment amount', function (value, context) {
                        const { parent } = context;
                        return Number(value) <= Number(`${parent?.commitmentAmount ?? ""}`?.replaceAll(',', ''))
                    })
            }))
        }

        if (event.target.value === "Redemption") {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                // NOTE: quantity is required only when redeemType === "Quantity".
                // redeemType-aware validation is enforced at the field level via the
                // redeemType value in Formik context; base validation here allows both modes.
                quantity: yup
                    .string()
                    .when('redeemType', {
                        is: (val: string) => (val ?? "Quantity") === "Quantity",
                        then: (schema) => schema
                            .required('Quantity is required')
                            .test('is-number', 'Quantity should be a valid number', function (value) {
                                return !Number.isNaN(Number(value))
                            })
                            .test('greater-than-zero', "Quantity should be greater than 0", function (value) {
                                return Number(value) > 0
                            })
                            .test("less-than-equal-to-outstanding-units", 'Quantity should be less than or equal to Outstanding Units', function (value, context) {
                                const { parent } = context;
                                return Number(value) <= Number(parent?.selectedFolioData?.qtySaleable ?? parent?.security?.qtySaleable ?? "")
                            }),
                        otherwise: (schema) => schema
                    }),
                // NOTE (AIF-CA-01): Redemption commitment amount is always API-sourced and the field is
                // always disabled — even if the API fails. This is intentional.
                // The user cannot manually enter a value for this transaction type.
                commitmentAmount: yup.string(),
                amount: yup
                    .string()
                    .when('redeemType', {
                        is: (val: string) => (val ?? "Quantity") === "Amount",
                        then: (schema) => schema
                            .transform(function (value) {
                                return `${value ?? ""}`?.replaceAll(',', '')
                            })
                            .required('Amount is required')
                            .test('is-number', 'Amount should be a valid number', function (value) {
                                return !Number.isNaN(Number(value))
                            })
                            .test('greater-than-zero', "Amount should be greater than 0", function (value) {
                                return Number(value) > 0
                            }),
                        otherwise: (schema) => schema
                    })
            }))
        }

        if (event.target.value === "Top Up") {
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                quantity: yup.string(),
                commitmentAmount: yup
                    .string()
                    .transform(function (value) {
                        return `${value ?? ""}`?.replaceAll(',', '')
                    })
                    .required('Commitment Amount is required')
                    .test('is-number', 'Commitment Amount should be a valid number', function (value) {
                        return !Number.isNaN(Number(value))
                    })
                    .test('greater-than-zero', "Commitment Amount should be greater than 0", function (value) {
                        return Number(value) > 0
                    }),
                // .test('whole-number', 'Commitment Amount should be a valid number', function (value) {
                //     return numberRegex.test(`${value ?? ""}`);
                // }),
                amount: yup
                    .string()
                    .transform(function (value) {
                        return `${value ?? ""}`?.replaceAll(',', '')
                    })
                    .required('Amount is required')
                    .test('is-number', 'Amount should be a valid number', function (value) {
                        return !Number.isNaN(Number(value))
                    })
                    .test('greater-than-zero', "Amount should be greater than 0", function (value) {
                        return Number(value) > 0
                    })
                    .test("less-than-equal-to-additional-commitment", 'Amount should not be more than Additional Commitment Amount', function (value, context) {
                        const { parent } = context;
                        return Number(value) <= Number(`${parent?.commitmentAmount ?? ""}`?.replaceAll(',', ''))
                    })
                // .test('whole-number', 'Amount should be a valid number', function (value) {
                //     return numberRegex.test(`${value ?? ""}`);
                // })
            }))
        }

        setTopUpPastCommitmentAmount(null)
        resetForm({
            values: {
                ...values,
                transactionType: event.target.value,
                security: null,
                folioNumber: "",
                selectedFolioData: null,
                issuer: "",
                quantity: "",
                lastAvailableNav: "",
                currentMarketValue: "",
                commitmentAmount: "",
                amount: "",
                redeemType: "Quantity",
                redeemAll: false,
                settlementBankNumber: null,
                settlementBank: "",
                settlementBankIfsc: "",
                settlementMethod: ""
            }
        })
    }

    const handleSecurityChange = (selectedObj: Record<string, any>) => {
        // For Initial Contribution: populate calculations from security (old flow — no folio involved)
        // For all other types: only set security+issuer, calculations happen in handleFolioChange
        setTimeout(() => {
            const isInitialContribution = values?.transactionType === "Initial Contribution"
            const calculatedMarketValue = Number(selectedObj?.qtySaleable ?? "") * Number(selectedObj?.marketPrice ?? "")

            resetForm({
                values: {
                    ...values,
                    security: selectedObj ? {
                        ...selectedObj,
                        bankAccNo: resolvePanPlaceholder(selectedObj?.bankAccNo ?? "", values?.clientName?.panNo ?? ""),
                        bankAccCode: resolvePanPlaceholder(selectedObj?.bankAccCode ?? "", values?.clientName?.panNo ?? ""),
                        folioNoList: (selectedObj?.folioNoList ?? []).map((obj: Record<string, any>) => ({
                            ...obj,
                            bankAccNo: resolvePanPlaceholder(obj?.bankAccNo ?? "", values?.clientName?.panNo ?? ""),
                            bankAccCode: resolvePanPlaceholder(obj?.bankAccCode ?? "", values?.clientName?.panNo ?? ""),
                        }))
                    } : null,
                    folioNumber: "",
                    selectedFolioData: null,
                    issuer: selectedObj?.issuerName ?? "",
                    quantity: isInitialContribution ? 0 : "",
                    lastAvailableNav: isInitialContribution && selectedObj
                        ? Number(selectedObj?.marketPrice ?? "")?.toLocaleString(locale)
                        : "",
                    currentMarketValue: isInitialContribution && selectedObj
                        ? calculatedMarketValue?.toLocaleString(locale)
                        : "",
                    commitmentAmount: "",
                    amount: "",
                    settlementBankNumber: isInitialContribution && selectedObj && selectedObj?.bankAccNo
                        ? {
                            ...(selectedObj ?? {}),
                            bankCode: selectedObj?.bankCode ?? "",
                            bankAccCcy: selectedObj?.bankAccCcy ?? "",
                            bankAccType: selectedObj?.bankAccType ?? "",
                            bankAccNo: resolvePanPlaceholder(selectedObj?.bankAccNo ?? "", values?.clientName?.panNo ?? ""),
                            bankAccCode: resolvePanPlaceholder(selectedObj?.bankAccCode ?? "", values?.clientName?.panNo ?? ""),
                            bankName: selectedObj?.bankName ?? "",
                            label: `${resolvePanPlaceholder(selectedObj?.bankAccNo ?? "", values?.clientName?.panNo ?? "")} ${selectedObj?.bankName ?? ""}`,
                            value: resolvePanPlaceholder(selectedObj?.bankAccNo ?? "", values?.clientName?.panNo ?? "")
                        }
                        : null,
                    settlementBank: isInitialContribution && selectedObj?.bankAccNo ? selectedObj?.bankName ?? "" : "",
                    settlementBankIfsc: isInitialContribution && selectedObj?.bankAccNo ? selectedObj?.ifscCode ?? "" : ""
                }
            })
        }, 50)
    }

    const handleFolioChange = async (selectedFolioNo: string) => {
        // Find the matching folio object from the selected security's folioNoList
        const folioNoList: Record<string, any>[] = values?.security?.folioNoList ?? []
        const folioObj = folioNoList.find((obj) => obj?.folioNo === selectedFolioNo) ?? null

        if (!folioObj) {
            resetForm({
                values: {
                    ...values,
                    folioNumber: selectedFolioNo,
                    selectedFolioData: null,
                    quantity: "", // NOTE (CORR-31): reset quantity regardless of transaction type for consistency with the clear button
                    lastAvailableNav: "",
                    currentMarketValue: "",
                    commitmentAmount: "",
                    amount: "",
                    settlementBankNumber: null,
                    settlementBank: "",
                    settlementBankIfsc: ""
                }
            })
            return
        }

        // Run all calculations using the folio's data (previously done on security select)
        const calculatedMarketValue = Number(folioObj?.qtySaleable ?? "") * Number(folioObj?.marketPrice ?? "")

        resetForm({
            values: {
                ...values,
                folioNumber: selectedFolioNo,
                selectedFolioData: folioObj,
                lastAvailableNav: Number(folioObj?.marketPrice ?? "")?.toLocaleString(locale),
                currentMarketValue: calculatedMarketValue?.toLocaleString(locale),
                quantity: ["Initial Contribution", "Drawdown Contribution", "Top Up"].includes(values?.transactionType) ? 0 : "",
                commitmentAmount: "",
                amount: "",
                settlementBankNumber: {
                    ...(folioObj ?? {}),
                    bankCode: folioObj?.bankCode ?? "",
                    bankAccCcy: folioObj?.bankAccCcy ?? "",
                    bankAccType: folioObj?.bankAccType ?? "",
                    bankAccNo: resolvePanPlaceholder(folioObj?.bankAccNo ?? "", values?.clientName?.panNo ?? ""),
                    bankAccCode: resolvePanPlaceholder(folioObj?.bankAccCode ?? "", values?.clientName?.panNo ?? ""),
                    bankName: folioObj?.bankName ?? "",
                    label: `${resolvePanPlaceholder(folioObj?.bankAccNo ?? "", values?.clientName?.panNo ?? "")} ${folioObj?.bankName ?? ""}`,
                    value: resolvePanPlaceholder(folioObj?.bankAccNo ?? "", values?.clientName?.panNo ?? "")
                },
                settlementBank: folioObj?.bankName ?? "",
                settlementBankIfsc: folioObj?.ifscCode ?? ""
            }
        })

        // Reset Top Up past commitment amount whenever a new folio is selected
        setTopUpPastCommitmentAmount(null)

        // Call holdingCommitmentInfo API to get the commitment amount for this folio
        try {
            setIsFetchingCommitmentInfo(true)
            // NOTE (CORR-01): values?.account holds the portfolioId set by the required Account
            // dropdown in Client Details — it is always populated before the user can reach folio selection.
            const response = await fetchHoldingCommitmentInfo(
                values?.security?.security ?? "",
                values?.account ?? "",
                selectedFolioNo
            )
            if (response?.isSuccess && response?.data?.commitmentAmount != null) {
                const fetchedAmount = Number(response.data.commitmentAmount)
                const formattedAmount = (!Number.isNaN(fetchedAmount) ? fetchedAmount : 0)?.toLocaleString(locale, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 4
                })
                if (values?.transactionType === "Top Up") {
                    // For Top Up: store the past commitment in the label state only.
                    // The commitmentAmount (now "Additional Commitment Amount") field is left empty for user input.
                    setTopUpPastCommitmentAmount(!Number.isNaN(fetchedAmount) ? fetchedAmount : 0)
                } else {
                    setFieldValue("commitmentAmount", formattedAmount)
                }
            } else {
                // API succeeded but returned no usable value — treat same as failure.
                // NOTE (AIF-CA-01): For Drawdown Contribution and Redemption, the field is ALWAYS
                // disabled regardless of API outcome — this is intentional. The backend guarantees
                // commitmentAmount is never empty for valid orders of these types; no client-side
                // error or blocking validation is required.
                // For Top Up: label defaults to 0, field stays empty for user input.
                if (values?.transactionType === "Top Up") {
                    setTopUpPastCommitmentAmount(0)
                }
            }
        } catch {
            // API failed — field remains disabled for Drawdown/Redemption (see NOTE AIF-CA-01 above).
            // For Top Up: label defaults to 0, field stays empty for user input.
            if (values?.transactionType === "Top Up") {
                setTopUpPastCommitmentAmount(0)
            }
        } finally {
            setIsFetchingCommitmentInfo(false)
        }
    }

    const handleQuantityChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        resetForm({
            values: {
                ...values,
                quantity: event.target.value,
                redeemAll: false
            }
        })
    }

    const handleRedeemAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked
        const outstandingUnits = `${values?.selectedFolioData?.qtySaleable ?? values?.security?.qtySaleable ?? ""}`
        resetForm({
            values: {
                ...values,
                redeemAll: checked,
                quantity: checked ? outstandingUnits : ""
            }
        })
    }

    // NOTE (CORR-02/CORR-03): items were previously hardcoded to []. Now populated from
    // values.security.folioNoList — each security in the holdings API response contains a
    // folioNoList array with all folios for that holding. The reviewer's diff was stale.
    const refactoredCurrentMarketValue = `${values?.currentMarketValue ?? ""}`?.replaceAll(',', '')
    const refactoredCommitmentAmount = `${values?.commitmentAmount ?? ""}`?.replaceAll(',', '')
    const refactoredAmount = `${values?.amount ?? ""}`?.replaceAll(',', '')
    // redeemType drives which input (Quantity or Amount) is shown for Redemption
    const redeemType: string = values?.redeemType ?? "Quantity"
    const refactoredPastCommitmentAmount = formatCurrency({
        // For Top Up: use the dedicated API-fetched state (field is left empty for user input)
        // For other folio-based types: mirror the commitmentAmount field value
        // Initial Contribution has no past commitment so always shows 0
        value: values?.transactionType === "Top Up"
            ? (topUpPastCommitmentAmount ?? 0)
            : values?.transactionType !== "Initial Contribution" && !Number.isNaN(Number(refactoredCommitmentAmount)) && refactoredCommitmentAmount !== ""
                ? Number(refactoredCommitmentAmount)
                : 0,
        locale,
        currencyCode
    })
    // Formatted past contributed amount for display above the Amount field (Drawdown and Top Up only)
    const refactoredPastContributedAmount = formatCurrency({
        value: (() => {
            const raw = Number(values?.selectedFolioData?.pastContribution ?? "")
            return !Number.isNaN(raw) ? raw : 0
        })(),
        locale,
        currencyCode
    })

    // Total Commitment Amount = Past Commitment + Additional Commitment (Top Up only)
    const additionalCommitmentRaw = Number(`${values?.commitmentAmount ?? ""}`?.replaceAll(',', ''))
    const pastCommitmentForTotal = topUpPastCommitmentAmount ?? 0
    const totalCommitmentValue = !Number.isNaN(additionalCommitmentRaw) && additionalCommitmentRaw > 0
        ? pastCommitmentForTotal + additionalCommitmentRaw
        : pastCommitmentForTotal
    const refactoredTotalCommitmentAmount = formatCurrency({ value: totalCommitmentValue, locale, currencyCode })


    return (
        <Grid
            container
            columnSpacing={3}
        >
            {/* <Grid item xs={12} lg={4}>
                <MFTextField
                    name="orderNumber"
                    label="Order Number"
                    placeholder=""
                />
            </Grid> */}
            <Grid item xs={12} lg={4}>
                <TextDatePicker
                    name="orderDate"
                    label="Order Date *"
                    placeholder=""
                    maxDate={
                        businessDate ?
                            dayjs(businessDate, "DD/MM/YYYY").toDate() :
                            new Date()
                    }
                    customHandleChange={handleOrderDateChange}
                    disabled
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFSelectField
                    name="transactionType"
                    label="Transaction Type *"
                    items={[
                        { name: "Redemption" }, //call holding api
                        { name: "Initial Contribution" },
                        { name: "Drawdown Contribution" }, //call holdings api
                        { name: "Top Up" } //call holding api
                    ].map((obj) => ({
                        key: obj.name,
                        value: obj.name,
                    }))}
                    disabled={isLoading || !values?.clientName}
                    onChange={handleTransactionTypeChange}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <FormikSearchDropdown
                    name="security"
                    label="Security *"
                    options={securitiesData?.securities?.map((obj: Record<string, any>) => ({
                        ...obj,
                        label: `${obj?.securityName ?? ""}${obj?.isin ? ` (${obj.isin})` : ""}`,
                        value: obj?.security ?? ""
                    })) ?? []}
                    fetchData={fetchSecuritiesData}
                    disabled={isLoading || !values?.transactionType}
                    onChange={handleSecurityChange}
                    filterOptions={(x) => x}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="issuer"
                    label="Issuer"
                    placeholder=""
                    disabled={true}
                    value={values?.issuer ?? ""}
                />
            </Grid>
            {(["Initial Contribution"].includes(values.transactionType)) && (
                <Grid item xs={12} lg={4}>
                    <Box sx={{ position: "relative" }}>
                        <MFTextField
                            name="folioNumber"
                            label="Folio Number"
                            placeholder=""
                            onKeyDown={preventSpecialCharacters}
                            disabled={isLoading || !values?.security}
                            value={values?.folioNumber ?? ""}
                        />
                        {values?.folioNumber && !isLoading && (
                            <IconButton
                                size="small"
                                onClick={() => setFieldValue("folioNumber", "")}
                                sx={{
                                    position: "absolute",
                                    right: "8px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    padding: "2px"
                                }}
                                aria-label="Clear folio number"
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Grid>
            )}
            {(["Redemption", "Drawdown Contribution", "Top Up"].includes(values.transactionType)) && (
                <Grid item xs={12} lg={4}>
                    <Box sx={{ position: "relative" }}>
                        <MFSelectField
                            name="folioNumber"
                            label="Folio Number *"
                            items={(values?.security?.folioNoList ?? [])
                                .filter((obj: Record<string, any>) => obj?.folioNo)
                                .map((obj: Record<string, any>) => ({
                                    key: obj.folioNo,
                                    value: obj.folioNo,
                                }))}
                            disabled={isLoading || isFetchingCommitmentInfo || !values?.security}
                            value={values?.folioNumber ?? ""}
                            onChange={(e) => handleFolioChange(e.target.value as string)}
                        />
                        {values?.folioNumber && !isLoading && (
                            <IconButton
                                size="small"
                                onClick={() => {
                                    resetForm({
                                        values: {
                                            ...values,
                                            folioNumber: "",
                                            selectedFolioData: null,
                                            lastAvailableNav: "",
                                            currentMarketValue: "",
                                            commitmentAmount: "",
                                            amount: "",
                                            quantity: "", // reset quantity for all types — consistent with handleFolioChange !folioObj branch (CORR-31)
                                            settlementBankNumber: null,
                                            settlementBank: "",
                                            settlementBankIfsc: ""
                                        }
                                    })
                                }}
                                sx={{
                                    position: "absolute",
                                    right: "36px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    padding: "2px",
                                    zIndex: 1
                                }}
                                aria-label="Clear folio number"
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Grid>
            )}
            {/* Redeem Type dropdown — only for Redemption */}
            {values?.transactionType === "Redemption" && (
                <Grid item xs={12} lg={4}>
                    <MFSelectField
                        name="redeemType"
                        label="Redeem Type *"
                        items={[
                            { key: "Quantity", value: "Quantity" },
                            { key: "Amount", value: "Amount" }
                        ]}
                        disabled={isLoading || !values?.folioNumber}
                        value={redeemType}
                        onChange={(e) => {
                            resetForm({
                                values: {
                                    ...values,
                                    redeemType: e.target.value as string,
                                    quantity: "",
                                    amount: "",
                                    redeemAll: false
                                }
                            })
                        }}
                    />
                </Grid>
            )}
            {/* Quantity field — shown when not Redemption, or when Redemption + redeemType is Quantity */}
            {(values?.transactionType !== "Redemption" || redeemType === "Quantity") && (
                <Grid item xs={12} lg={4} sx={{ position: "relative" }}>
                    {values?.selectedFolioData && (
                        <Typography
                            variant="overline_400"
                            sx={{
                                position: "absolute",
                                top: "-5px",
                                right: "0px"
                            }}
                        >
                            Outstanding Units: {values?.selectedFolioData?.qtySaleable ?? ""}
                        </Typography>
                    )}
                    <MFTextField
                        name="quantity"
                        label={`Quantity ${["Redemption"].includes(values.transactionType) ? "*" : ""}`}
                        placeholder=""
                        disabled={
                            isLoading ||
                            !values?.security ||
                            (["Redemption"].includes(values.transactionType) && !values?.folioNumber) ||
                            (["Initial Contribution", "Drawdown Contribution", "Top Up"].includes(values.transactionType)) ||
                            (values?.transactionType === "Redemption" && !!values?.redeemAll)
                        }
                        value={values?.quantity ?? ""}
                        formatCurrency
                        formatCurrencyLocaleOptions={{
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 4,
                            useGrouping: false
                        }}
                        onChange={handleQuantityChange}
                    />
                    {/* Redeem All checkbox — only for Redemption in Quantity mode */}
                    {values?.transactionType === "Redemption" && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!values?.redeemAll}
                                    onChange={handleRedeemAllChange}
                                    disabled={isLoading || !values?.folioNumber}
                                    size="small"
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.primary[500],
                                        padding: "2px 4px 2px 0px"
                                    })}
                                />
                            }
                            label={
                                <Typography variant="overline_400" sx={{ fontWeight: 600 }}>
                                    Redeem All
                                </Typography>
                            }
                            sx={{ mt: "-12px", ml: 0 }}
                        />
                    )}
                </Grid>
            )
            }
            {/* Amount field in Redemption + Amount mode */}
            {
                values?.transactionType === "Redemption" && redeemType === "Amount" && (
                    <Grid item xs={12} lg={4}>
                        <MFTextField
                            name="amount"
                            label="Amount *"
                            placeholder=""
                            startAdornment={values?.amount ? (
                                <Typography
                                    variant='body_500'
                                    sx={(theme: Theme) => ({
                                        color: (isLoading || !values?.security || !values?.folioNumber) ? theme.palette.text.secondary : theme.palette.text.primary
                                    })}
                                >
                                    {String.fromCharCode(parseInt(currencySymbol, 16))}
                                </Typography>
                            ) : undefined}
                            sx={{
                                '&.MuiOutlinedInput-root': {
                                    "& fieldset>legend": {
                                        fontSize: "0.78em",
                                    }
                                }
                            }}
                            disabled={isLoading || !values?.security || !values?.folioNumber}
                            onKeyDown={allowDecimalNumbers}
                            formatCurrency
                            formatCurrencyLocaleOptions={{
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 4,
                            }}
                            onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handleCurrencyFieldPaste(event, "amount")}
                            value={values?.amount ?? ""}
                        />
                        <CurrencyConversionToWords value={refactoredAmount} />
                    </Grid>
                )
            }
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="currentMarketValue"
                    label="Current Market Value"
                    placeholder=""
                    startAdornment={values?.currentMarketValue ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    formatCurrency
                    disabled={true}
                    value={values?.currentMarketValue ?? ""}
                    onKeyDown={allowDecimalNumbers}
                />
                <CurrencyConversionToWords value={refactoredCurrentMarketValue} />
            </Grid>
            <Grid item xs={12} lg={4} sx={{ position: "relative" }}>
                {values?.transactionType && values?.transactionType !== "Initial Contribution" && (
                    <Typography
                        variant="overline_400"
                        sx={{
                            position: "absolute",
                            top: "-5px",
                            right: "0px"
                        }}
                    >
                        Past Commitment Amount: {refactoredPastCommitmentAmount}
                    </Typography>
                )}
                <MFTextField
                    name="commitmentAmount"
                    label={
                        values.transactionType === "Top Up"
                            ? "Additional Commitment Amount *"
                            : "Commitment Amount"
                    }
                    placeholder={
                        values.transactionType === "Top Up"
                            ? "Additional Commitment Amount"
                            : ""
                    }
                    startAdornment={values?.commitmentAmount ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({
                                color: (
                                    isLoading ||
                                    isFetchingCommitmentInfo ||
                                    !values?.security ||
                                    // NOTE (AIF-CA-01): Drawdown/Redemption field is always disabled — always secondary color
                                    ["Drawdown Contribution", "Redemption"].includes(values?.transactionType)
                                ) ? theme.palette.text.secondary : theme.palette.text.primary
                            })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    disabled={
                        isLoading ||
                        isFetchingCommitmentInfo ||
                        !values?.security ||
                        // NOTE (AIF-CA-01): Drawdown Contribution and Redemption are ALWAYS disabled —
                        // commitment amount is always API-sourced for these types and the field must
                        // not be editable under any circumstance, including API failure. The backend
                        // guarantees commitmentAmount is never empty for valid Drawdown/Redemption
                        // orders — no client-side blocking error or validation is needed.
                        ["Drawdown Contribution", "Redemption"].includes(values?.transactionType) ||
                        // Folio must be selected before commitment amount can be entered (Top Up / Drawdown / Redemption)
                        (["Redemption", "Drawdown Contribution", "Top Up"].includes(values?.transactionType) && !values?.folioNumber)
                    }
                    onKeyDown={allowDecimalNumbers}
                    formatCurrency
                    formatCurrencyLocaleOptions={{
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 4,
                    }}
                    onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handleCurrencyFieldPaste(event, "commitmentAmount")}
                    value={values?.commitmentAmount ?? ""}
                />
                <CurrencyConversionToWords value={refactoredCommitmentAmount} />
            </Grid>
            {/* Total Commitment Amount — shown only for Top Up, disabled, = Past Commitment + Additional Commitment */}
            {values?.transactionType === "Top Up" && (
                <Grid item xs={12} lg={4}>
                    <MFTextField
                        name="totalCommitmentAmount"
                        label="Total Commitment Amount"
                        placeholder=""
                        startAdornment={
                            <Typography
                                variant='body_500'
                                sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                            >
                                {String.fromCharCode(parseInt(currencySymbol, 16))}
                            </Typography>
                        }
                        endAdornment={
                            <CustomTooltip
                                titleContent={
                                    <Typography variant='subtitle_600'>
                                        Sum of Past Commitment Amount and Additional Commitment Amount
                                    </Typography>
                                }
                            />
                        }
                        sx={{
                            '&.MuiOutlinedInput-root': {
                                "& fieldset>legend": {
                                    fontSize: "0.78em",
                                }
                            }
                        }}
                        disabled
                        value={totalCommitmentValue?.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 4 })}
                    />
                </Grid>
            )}
            {/* Amount field for non-Redemption types (Initial Contribution, Drawdown, Top Up) */}
            {values?.transactionType !== "Redemption" && (
                <Grid item xs={12} lg={4} sx={{ position: "relative", mt: "12px" }}>
                    {["Drawdown Contribution", "Top Up"].includes(values?.transactionType) && values?.folioNumber && (
                        <Typography
                            variant="overline_400"
                            sx={{
                                position: "absolute",
                                top: "-5px",
                                right: "0px"
                            }}
                        >
                            Past Contributed Amount: {refactoredPastContributedAmount}
                        </Typography>
                    )}
                    <MFTextField
                        name="amount"
                        label={`Amount *`}
                        placeholder=""
                        startAdornment={values?.amount ? (
                            <Typography
                                variant='body_500'
                                sx={(theme: Theme) => ({
                                    color: (isLoading || !values?.security) ? theme.palette.text.secondary : theme.palette.text.primary
                                })}
                            >
                                {String.fromCharCode(parseInt(currencySymbol, 16))}
                            </Typography>
                        ) : undefined}
                        sx={{
                            '&.MuiOutlinedInput-root': {
                                "& fieldset>legend": {
                                    fontSize: "0.78em",
                                }
                            }
                        }
                        }
                        disabled={isLoading || !values?.security || (["Redemption", "Drawdown Contribution", "Top Up"].includes(values?.transactionType) && !values?.folioNumber)}
                        onKeyDown={allowDecimalNumbers}
                        formatCurrency
                        formatCurrencyLocaleOptions={{
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 4,
                        }}
                        onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handleCurrencyFieldPaste(event, "amount")}
                        value={values?.amount ?? ""}
                    />
                    <CurrencyConversionToWords value={refactoredAmount} />
                </Grid>
            )}
            <Grid item xs={12} sx={{ marginTop: "20px" }}>
                <Typography
                    variant="body_500"
                    sx={(theme: Theme) => ({
                        lineHeight: "20px",
                        letterSpacing: "0.02px",
                        color: theme.palette.text.secondary
                    })}
                >
                    Deferral/Exception
                </Typography>
                <FormControl sx={{ marginTop: "15px" }}>
                    <RadioGroup
                        row
                        value={values?.deferralException}
                        onChange={handleDeferralExceptionChange}
                        defaultValue="No"
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            rowGap: "10px"
                        }}
                    >
                        {[
                            { deferralExceptionLabel: "Yes", deferralExceptionValue: "Yes" },
                            { deferralExceptionLabel: "No", deferralExceptionValue: "No" }
                        ].map(({ deferralExceptionLabel, deferralExceptionValue }) => (
                            <FormControlLabel
                                key={deferralExceptionValue}
                                value={deferralExceptionValue}
                                control={
                                    <Radio
                                        size="medium"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.primary[500],
                                            "&.MuiRadio-root": {
                                                padding: "0px",
                                            },
                                            ...(values?.deferralException !== deferralExceptionValue ? {
                                                stroke: theme.palette.primary[50],
                                            } : {}),
                                        })}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            component="span"
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "20px",
                                                letterSpacing: '0.02px',
                                                color: values?.deferralException === deferralExceptionValue ?
                                                    theme.palette.primary[500] :
                                                    theme.palette.common.black,
                                            })}
                                        >
                                            {deferralExceptionLabel}
                                        </Typography>
                                    </Box>
                                }
                                sx={(theme: Theme) => ({
                                    borderRadius: '10px',
                                    backgroundColor: values?.deferralException === deferralExceptionValue ?
                                        theme.palette.primary[50] :
                                        `${theme.palette.common.white}0D`,
                                    padding: values?.deferralException === deferralExceptionValue ? '7px 12px' : '8px 12px',
                                    border: values?.deferralException === deferralExceptionValue ?
                                        `1px solid ${theme.palette.primary[500]}` :
                                        `0.5px solid ${theme.palette.text.secondary}80`,
                                    "&.MuiFormControlLabel-root": {
                                        columnGap: "10px",
                                        marginLeft: "0px",
                                        marginRight: "12px"
                                    },
                                })}
                                disabled={isLoading}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ marginTop: "20px" }}>
                <MFTextField
                    name="remarks"
                    label="Remarks *"
                    placeholder=""
                    multiline
                    inputProps={{ maxLength: 200 }}
                    value={values?.remarks ?? ""}
                    disabled={isLoading}
                />
                <Typography
                    variant="overline_400"
                    sx={(theme: Theme) => ({
                        textAlign: "end",
                        color: theme.palette.text.secondary,
                        marginTop: "-10px"
                    })}
                >
                    Max 200 characters
                </Typography>
            </Grid>
        </Grid >
    )
}